import { audioBus } from './audio';

/**
 * Hand-synthesized SFX presets. Each preset computes an AudioBuffer on demand
 * and caches it. Reproducibility is not required here since this is renderer
 * layer only, so Math.random for noise is fine.
 */

type Preset = () => AudioBuffer | null;

const cache = new Map<string, AudioBuffer>();

function getCtx(): AudioContext | null {
  return audioBus.getContext();
}

function makeBuffer(durationSec: number, fill: (t: number, i: number, sr: number) => number): AudioBuffer | null {
  const ctx = getCtx();
  if (!ctx) return null;
  const sr = ctx.sampleRate;
  const len = Math.max(1, Math.floor(sr * durationSec));
  const buf = ctx.createBuffer(1, len, sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    data[i] = fill(t, i, sr);
  }
  return buf;
}

function envExp(t: number, durationSec: number, decayMul = 6): number {
  const f = t / durationSec;
  if (f >= 1) return 0;
  return Math.exp(-f * decayMul);
}

function sine(t: number, freq: number): number {
  return Math.sin(2 * Math.PI * freq * t);
}

function square(t: number, freq: number): number {
  return Math.sin(2 * Math.PI * freq * t) >= 0 ? 1 : -1;
}

function saw(t: number, freq: number): number {
  const p = (t * freq) % 1;
  return p * 2 - 1;
}

function noise(): number {
  return Math.random() * 2 - 1;
}

const presets: Record<string, Preset> = {
  shoot_spread: () =>
    makeBuffer(0.08, (t) => square(t, 880 + t * 200) * envExp(t, 0.08, 10) * 0.3),

  shoot_laser: () =>
    makeBuffer(0.15, (t) => {
      const f = 1200 - t * 5000;
      return saw(t, Math.max(80, f)) * envExp(t, 0.15, 6) * 0.25;
    }),

  shoot_homing: () =>
    makeBuffer(0.12, (t) => (sine(t, 660) * 0.6 + noise() * 0.4) * envExp(t, 0.12, 8) * 0.25),

  hit_enemy: () => makeBuffer(0.08, (t) => noise() * envExp(t, 0.08, 14) * 0.3),

  hit_player: () =>
    makeBuffer(0.18, (t) => square(t, 120) * envExp(t, 0.18, 6) * 0.4),

  explode_small: () =>
    makeBuffer(0.15, (t) => {
      const n = noise();
      // simple low-pass-ish: average with itself shifted (approximation)
      return n * envExp(t, 0.15, 7) * 0.4;
    }),

  explode_big: () =>
    makeBuffer(0.4, (t) => {
      const f = 180 - t * 250;
      const base = sine(t, Math.max(40, f));
      const n = noise();
      return (base * 0.5 + n * 0.5) * envExp(t, 0.4, 5) * 0.5;
    }),

  pickup: () =>
    makeBuffer(0.22, (t) => {
      const freq = t < 0.11 ? 660 : 990;
      return sine(t, freq) * envExp(t, 0.22, 6) * 0.3;
    }),

  bomb: () =>
    makeBuffer(0.6, (t) => {
      const base = sine(t, 60 + t * 40);
      return (base * 0.4 + noise() * 0.6) * envExp(t, 0.6, 3) * 0.55;
    }),

  boss_phase: () =>
    makeBuffer(0.5, (t) => sine(t, 220 + t * 600) * envExp(t, 0.5, 4) * 0.35),

  gameover: () =>
    makeBuffer(0.8, (t) => sine(t, 440 - t * 400) * envExp(t, 0.8, 3) * 0.4),

  titleStart: () =>
    makeBuffer(0.35, (t) => {
      const s = sine(t, 440) * 0.4 + sine(t, 554) * 0.3 + sine(t, 659) * 0.3;
      return s * envExp(t, 0.35, 4) * 0.4;
    }),
};

/** Play a preset by name. No-op if audio is not unlocked or preset missing. */
export function playSfx(name: keyof typeof presets | string): void {
  if (!audioBus.isUnlocked()) return;
  let buf = cache.get(name);
  if (!buf) {
    const maker = presets[name];
    if (!maker) return;
    const built = maker();
    if (!built) return;
    buf = built;
    cache.set(name, buf);
  }
  audioBus.playBuffer(buf);
}
